import React, { useState, useEffect } from 'react';
import styles from './styles.module.scss';
import './styles.css';
import CustomInput from '../../../../../components/CustomInput';
import Button from '../../../../../components/Button';
import Flex from '../../../../../components/Flex';
import Select from 'react-select';
import { commonImages, operatorImages } from '../../../../../assets/images';
import DateRangePicker from 'react-daterange-picker';
import 'react-daterange-picker/dist/css/react-calendar.css';
import moment from 'moment';
import PermitsHeader from '../../../../../components/PermitsHeader';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import dimensions from '../../../../../util/dimensions-util';
import SannaLogo from '../../../../../components/SannaLogo';
import { TRANSACTION_REPORT_TABS } from '../../constants';

const { vw: widthConstant, vh: heightConstant } = dimensions;

const TransactionReportForm = ({
    email,
    emailError,
    dates,
    onChange,
    onClick,
    agencyList,
    selectedAgencyId,
}) => {
    const [showDate, setShowDate] = useState(false);

    const onSelect = selectedDates => {
        onChange('date', {
            startDate: moment(selectedDates.start).format('MM-DD-YYYY'),
            endDate: moment(selectedDates.end).format('MM-DD-YYYY'),
            dates: selectedDates,
        });
        setShowDate(false);
    };

    const toggleDate = () => {
        setShowDate(!showDate);
    };

    const formatDate = date => moment(date).format('MMM Do YY');

    const agencyOptions = [
        {
            value: 0,
            label: 'All',
            data: { id: 0, name: 'All' },
        },
        ...(agencyList || []).map(agency => ({
            value: agency.id,
            label: agency.name || agency.companyName,
            data: agency,
        })),
    ];

    const selectedAgencyValue =
        agencyOptions.find(opt => opt.value === selectedAgencyId) || null;

    const handleAgencyChange = selectedOption => {
        const value = selectedOption ? selectedOption.value : undefined;
        onChange('agency', value);
    };

    const minimumDate = moment().subtract(6, 'months').toDate();
    const maximumDate = new Date();

    return (
        <div className={styles.formSection}>
            <div className={styles.content}>
                <div className={styles.header}>
                    <p className={styles.subtitle}>
                        Enter email address and select date range to generate
                        transaction report (maximum 6 months old)
                    </p>
                </div>

                <div className={styles.formCard}>
                    <div className={styles.formGrid}>
                        <div className={styles.inputContainer}>
                            <label className={styles.inputLabel}>Agency</label>
                            <div className={styles.dropdownWrapper}>
                                <Select
                                    options={agencyOptions}
                                    value={selectedAgencyValue}
                                    onChange={handleAgencyChange}
                                    placeholder="Select Agency"
                                    isClearable
                                    className={styles.selectDropdown}
                                    classNamePrefix="react-select"
                                />
                            </div>
                        </div>

                        <div className={styles.inputContainer}>
                            <CustomInput
                                inputLable="Email Address *"
                                placeholder="Enter email address"
                                value={email}
                                onTextChange={value => onChange('email', value)}
                                error={emailError}
                                required={true}
                            />
                        </div>

                        <div className={styles.inputContainer}>
                            <label className={styles.inputLabel}>Date Range *</label>
                            <div className={styles.dateWrapper}>
                                <Button
                                    onClick={toggleDate}
                                    buttonStyle="secondary"
                                    label={
                                        dates
                                            ? `${formatDate(dates.start)} - ${formatDate(
                                                  dates.end,
                                              )}`
                                            : 'Select Date Range'
                                    }
                                    className={styles.dateBtn}
                                />
                                {showDate ? (
                                    <div className={styles.datePicker}>
                                        <DateRangePicker
                                            numberOfCalendars={1}
                                            selectionType="range"
                                            onSelect={onSelect}
                                            className={styles.dateRange}
                                            value={dates}
                                            singleDateRange={true}
                                            minimumDate={minimumDate}
                                            maximumDate={maximumDate}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    <div className={styles.buttonContainer}>
                        <Button
                            label={
                                <Flex direction="row" className={styles.buttonContent}>
                                    <span>Send Report</span>
                                </Flex>
                            }
                            buttonStyle="primary"
                            onClick={() => onClick('sendReport')}
                            className={styles.submitButton}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const GeneratedReportsTab = ({ onClick, onChange, totalCount, pageSize }) => {
    const [gridApi, setGridApi] = useState(null);

    const onGridReady = params => {
        setGridApi(params.api);
        onChange('gridAPI', params.api);
    };

    useEffect(() => {
        if (gridApi && totalCount > 10) {
            gridApi.setDomLayout('normal');
        } else if (gridApi) {
            gridApi.setDomLayout('autoHeight');
        }
    }, [gridApi, totalCount]);

    useEffect(() => {
        if (gridApi) {
            onChange('updateTable');
        }
    }, [gridApi]);

    const defaultCellStyle = {
        display: 'flex',
        alignItems: 'center',
        lineHeight: 1.5,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
    };

    const formatTimestamp = value => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        const parsed = moment(Number(value));
        if (parsed.isValid()) {
            return parsed.format('D MMM YYYY HH:mm');
        }
        const textParsed = moment(value);
        return textParsed.isValid() ? textParsed.format('D MMM YYYY HH:mm') : '-';
    };

    const formatFileName = value =>
        value !== null && value !== undefined && value !== '' ? value : '-';

    const formatReportDate = value => {
        if (!value) {
            return null;
        }
        const mmDdYyyy = moment(value, 'MM-DD-YYYY', true);
        if (mmDdYyyy.isValid()) {
            return mmDdYyyy.format('D MMM YYYY');
        }
        const parsed = moment(value);
        return parsed.isValid() ? parsed.format('D MMM YYYY') : null;
    };

    const canDownload = row => {
        if (row?.status !== 'COMPLETED' || !row?.downloadUrl) {
            return false;
        }
        if (row?.expiresAt && moment().isAfter(moment(row.expiresAt))) {
            return false;
        }
        return true;
    };

    const formatStatusLabel = status => {
        if (!status) {
            return '-';
        }
        return status
            .split('_')
            .map(part => part.charAt(0) + part.slice(1).toLowerCase())
            .join(' ');
    };

    const renderStatus = status => {
        const label = formatStatusLabel(status);
        if (status === 'FAILED') {
            return <span className={styles.statusFailed}>{label}</span>;
        }
        if (status === 'IN_PROGRESS' || status === 'PENDING') {
            return <span className={styles.inProgress}>{label}</span>;
        }
        if (status === 'COMPLETED') {
            return <span className={styles.statusCompleted}>{label}</span>;
        }
        return <span>{label}</span>;
    };

    const columnDefs = [
        {
            headerName: 'File Name',
            field: 'fileName',
            headerClass: styles.rowHeader,
            flex: 1,
            minWidth: 280 * widthConstant,
            resizable: true,
            wrapText: true,
            cellStyle: defaultCellStyle,
            valueFormatter: params => formatFileName(params.value),
        },
        {
            headerName: 'Email',
            field: 'recipientEmails',
            headerClass: styles.rowHeader,
            width: 220 * widthConstant,
            resizable: true,
            wrapText: true,
            cellStyle: defaultCellStyle,
            valueFormatter: params =>
                formatFileName(params.value ?? params.data?.email),
        },
        {
            headerName: 'Created Date',
            field: 'createdDate',
            headerClass: styles.rowHeader,
            width: 200 * widthConstant,
            resizable: true,
            wrapText: true,
            cellStyle: defaultCellStyle,
            valueFormatter: params => formatTimestamp(params.value),
        },
        {
            headerName: 'Period',
            field: 'period',
            headerClass: styles.rowHeader,
            width: 280 * widthConstant,
            resizable: true,
            wrapText: true,
            cellStyle: defaultCellStyle,
            cellRendererFramework: params => {
                const row = params?.data;
                const startDate =
                    row?.filtersJson?.startDate ?? row?.startDate;
                const endDate = row?.filtersJson?.endDate ?? row?.endDate;
                const startLabel = formatReportDate(startDate);
                const endLabel = formatReportDate(endDate);
                if (!startLabel || !endLabel) {
                    return <span>-</span>;
                }
                return (
                    <span className={styles.period}>
                        {startLabel} - {endLabel}
                    </span>
                );
            },
        },
        {
            headerName: 'Status',
            field: 'status',
            headerClass: styles.rowHeader,
            width: 140 * widthConstant,
            resizable: true,
            wrapText: true,
            cellStyle: defaultCellStyle,
            cellRendererFramework: params =>
                renderStatus(params?.data?.status ?? params.value),
        },
        {
            headerName: 'Download',
            field: 'downloadUrl',
            headerClass: styles.rowHeader,
            width: 140 * widthConstant,
            resizable: true,
            cellStyle: defaultCellStyle,
            cellRendererFramework: params => {
                const row = params?.data;
                const isDownloadable = canDownload(row);

                return (
                    <button
                        type="button"
                        className={styles.downloadLink}
                        disabled={!isDownloadable}
                        onClick={() =>
                            isDownloadable && onClick('downloadReport', row)
                        }>
                        Download
                    </button>
                );
            },
        },
    ];

    const rowStyle = { border: 'none' };
    const getRowStyle = params => {
        if (params.node.rowIndex % 2 === 0) {
            return { background: '#f6f6f6', border: 'none' };
        }
    };

    return (
        <div className={styles.generatedSection}>
            <div className={styles.bodyContainer}>
                <div className={styles.listHeader}>
                    <div className={styles.listHeaderText}>
                        Download previously generated transaction report files.
                    </div>
                    <button
                        type="button"
                        className={styles.refreshBtn}
                        onClick={() => onClick('refreshReports')}>
                        Refresh
                    </button>
                </div>
                <div
                    className={[
                        'ag-theme-balham vehicleMovementTable',
                        styles.tableWrapper,
                        totalCount > 10
                            ? styles.tableWrapperNormal
                            : styles.tableWrapperAutoHeight,
                    ].join(' ')}>
                    <AgGridReact
                        onGridReady={onGridReady}
                        rowStyle={rowStyle}
                        getRowStyle={getRowStyle}
                        rowHoverHighlight={true}
                        columnDefs={columnDefs}
                        rowHeight={60 * heightConstant}
                        headerHeight={60 * heightConstant}
                        defaultColDef={{
                            sortable: true,
                            resizable: true,
                            wrapText: true,
                            cellStyle: defaultCellStyle,
                        }}
                        rowClass={styles.gridRowClass}
                        pagination={true}
                        rowModelType="infinite"
                        paginationPageSize={pageSize}
                        cacheBlockSize={pageSize}
                        domLayout="normal"
                        suppressCellSelection={true}
                    />
                </div>
            </div>
        </div>
    );
};

const SuccessPage = ({ onClick }) => {
    return (
        <div className={styles.formSection}>
            <div className={styles.content}>
                <div className={styles.statusCard}>
                    <div className={styles.statusIcon}>
                        <img
                            src={operatorImages.successIcon}
                            alt="Success"
                            className={styles.iconImage}
                        />
                    </div>
                    <h2 className={styles.statusTitle}>Report generation started!</h2>
                    <p className={styles.statusMessage}>
                        The transaction report is being prepared. You will receive it by
                        email when it is ready. You can also download it later from the
                        Generated Reports section.
                    </p>
                    <div className={styles.statusActions}>
                        <Button
                            label="View Generated Reports"
                            buttonStyle="primary"
                            onClick={() => onClick('viewGeneratedReports')}
                            className={styles.actionButton}
                        />
                        <Button
                            label="Okay"
                            buttonStyle="outline"
                            onClick={() => onClick('backToForm')}
                            className={styles.actionButton}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const FailurePage = ({ onClick }) => {
    return (
        <div className={styles.formSection}>
            <div className={styles.content}>
                <div className={styles.statusCard}>
                    <div className={styles.statusIcon}>
                        <img
                            src={commonImages.errorIcon}
                            alt="Error"
                            className={styles.iconImage}
                        />
                    </div>
                    <h2 className={styles.statusTitle}>Failed to Send Report</h2>
                    <p className={styles.statusMessage}>
                        There was an error sending the transaction report. Please check
                        your inputs and try again.
                    </p>
                    <div className={styles.statusActions}>
                        <Button
                            label="Try Again"
                            buttonStyle="primary"
                            onClick={() => onClick('retry')}
                            className={styles.actionButton}
                        />
                        <Button
                            label="Okay"
                            buttonStyle="outline"
                            onClick={() => onClick('backToForm')}
                            className={styles.actionButton}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function Desktop(props) {
    const {
        showSuccess,
        showFailure,
        activeTab,
        transactionReportTabs,
        onClick,
    } = props;

    const handleTabClick = id => {
        onClick('switchTab', id);
    };

    const renderBody = () => {
        if (showSuccess) {
            return <SuccessPage {...props} />;
        }
        if (showFailure) {
            return <FailurePage {...props} />;
        }
        if (activeTab === TRANSACTION_REPORT_TABS.GENERATED) {
            return <GeneratedReportsTab {...props} />;
        }
        return <TransactionReportForm {...props} />;
    };

    return (
        <div className={styles.pageWrapper}>
            <PermitsHeader
                title="Transaction Report"
                tabs={transactionReportTabs}
                activeTab={activeTab}
                onTabClick={handleTabClick}
            />
            {renderBody()}
            <div className={styles.SannaLogo}>
                <SannaLogo />
            </div>
        </div>
    );
}
